import React, { useState, useEffect, useMemo } from "react"
import Popup from "../../component/popup/popup"
import InputText from "../../component/inputText/inputText"
import Select from "../../component/select/select"
import Grid from "@mui/material/Grid"
import { GuideData } from "../../models"
import { useHotelGeneric } from "../../context/HotelGenericContext"
import { useSnackbar } from "../../context/snackbarContext"
import { agent } from "../../apiClient/api"
import { getActiveCityOptions } from "../../util/cityOptionsUtil"

type GuideEditPopupProps = {
    isOpen: boolean
    onClose: () => void
    guide: GuideData | null
    onSuccess?: () => void
}

const GuideEditPopup: React.FC<GuideEditPopupProps> = ({ isOpen, onClose, guide, onSuccess }) => {
    const { hotelGenericData } = useHotelGeneric()
    const { showError, showSuccess } = useSnackbar()

    const [cityId, setCityId] = useState("")
    const [basePrice, setBasePrice] = useState("")
    const [tippingTypeId, setTippingTypeId] = useState("")
    const [tipping, setTipping] = useState("")
    const [errors, setErrors] = useState<{
        cityId?: string
        basePrice?: string
        tippingTypeId?: string
        tipping?: string
    }>({})
    const [isSaving, setIsSaving] = useState(false)

    const tippingTypeOptions = useMemo(
        () =>
            (hotelGenericData?.tipping_type ?? []).map(
                (x: { tipping_type_id: number; tipping_type_name: string }) => ({
                    label: x.tipping_type_name,
                    value: String(x.tipping_type_id),
                })
            ),
        [hotelGenericData?.tipping_type]
    )

    useEffect(() => {
        if (isOpen && guide) {
            setCityId(String(guide.city_id ?? ""))
            setBasePrice(guide.base_price != null ? String(guide.base_price) : "")
            const initialTippingTypeId =
                guide.tipping_type_id != null ? String(guide.tipping_type_id) : ""
            setTippingTypeId(
                initialTippingTypeId || (tippingTypeOptions[0]?.value ?? "")
            )
            setTipping(guide.compulsory_tipping != null ? String(guide.compulsory_tipping) : "")
            setErrors({})
        }
    }, [isOpen, guide, tippingTypeOptions])

    const validate = (): boolean => {
        const next: {
            cityId?: string
            basePrice?: string
            tippingTypeId?: string
            tipping?: string
        } = {}
        if (!cityId?.trim()) next.cityId = "City is required"

        const basePriceNum = basePrice.trim() === "" ? NaN : Number(basePrice)
        if (basePrice.trim() === "" || isNaN(basePriceNum)) {
            next.basePrice = "Base Price is required"
        } else if (basePriceNum <= 0) {
            next.basePrice = "Base Price must be greater than zero"
        }

        if (!tippingTypeId?.trim()) next.tippingTypeId = "Tipping Type is required"

        const tippingNum = tipping.trim() === "" ? NaN : Number(tipping)
        if (tipping.trim() === "" || isNaN(tippingNum)) {
            next.tipping = "Tipping is required"
        } else if (tippingNum < 0) {
            next.tipping = "Tipping cannot be negative"
        }

        setErrors(next)
        return Object.keys(next).length === 0
    }

    const handleSave = async () => {
        if (!validate()) return
        const basePriceNum = Number(basePrice)
        const tippingNum = Number(tipping)
        if (basePriceNum <= 0 || tippingNum < 0) return

        setIsSaving(true)
        setErrors({})
        try {
            const response = await agent.GuidePutAPI.put({
                guide_id: guide?.guide_id ?? null,
                city_id: Number(cityId),
                base_price: basePriceNum,
                compulsory_tipping: tippingNum,
                tipping_type_id: Number(tippingTypeId),
                flag_active: guide?.flag_active ?? false
            })
            if (response?.status === true) {
                showSuccess("Guide updated successfully")
                await Promise.resolve(onSuccess?.())
                onClose()
            } else {
                showError(response?.error?.message ?? "Failed to update guide")
            }
        } catch (err) {
            console.error("Guide save error", err)
            showError("Failed to update guide")
        } finally {
            setIsSaving(false)
        }
    }

    const cityOptions = getActiveCityOptions(hotelGenericData?.city, cityId)

    if (!isOpen) return null

    return (
        <Popup
            isOpen={isOpen}
            onClose={onClose}
            title="Edit Guide"
            primaryButton={{ label: isSaving ? "Saving..." : "Save", onClick: handleSave, disabled: isSaving }}
            secondaryButton={{ label: "Cancel", onClick: onClose }}
            heightFiexd={true}
        >
            <Grid container spacing={2} style={{ paddingTop: 8 }}>
                <Grid size={{ xs: 12, md: 6 }}>
                    <Select
                        label="City"
                        value={cityId}
                        onChange={(e) => setCityId(e.target.value)}
                        options={cityOptions}
                        placeholder="Select City"
                        required
                        error={errors.cityId}
                        disabled={true}
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <InputText
                        label="Base Price"
                        type="number"
                        value={basePrice}
                        onChange={(e) => setBasePrice(e.target.value)}
                        placeholder="Enter Base Price (Per Day)"
                        required
                        error={errors.basePrice}
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <Select
                        label="Tipping Type"
                        value={tippingTypeId}
                        onChange={(e) => setTippingTypeId(e.target.value)}
                        options={tippingTypeOptions}
                        placeholder="Select Tipping Type"
                        required
                        error={errors.tippingTypeId}
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <InputText
                        label="Tipping"
                        type="number"
                        value={tipping}
                        onChange={(e) => setTipping(e.target.value)}
                        placeholder="Enter Tipping (Per Person Per Day)"
                        required
                        error={errors.tipping}
                    />
                </Grid>
            </Grid>
        </Popup>
    )
}

export default GuideEditPopup
